import { useState, useCallback, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSpinner } from '@fortawesome/free-solid-svg-icons';
import LiveInbox from '../components/LiveInbox.js';
import FirstBotPrompt from '../components/FirstBotPrompt.js';
import { useSelectedBot } from '../contexts/SelectedBotContext.js';
import { parseLiveDeepLink, selectBotFromDeepLink } from '../utils/deepLinks';
import '../components/liveInbox.css';
import './LiveChat.css';

// The bot is chosen once, globally, by the header BotSelector. This page reads
// it from SelectedBotContext instead of fetching bots and rendering its own
// "Select bot" dropdown. The context also handles `live-notify-open` /
// `ub:select-bot`, so opening a notification toast while on /live still
// switches the active bot without any local wiring.
//
// Deep links: notifications (Telegram / Slack / Teams / email / in-app toast)
// link to `/live?bot=<bot_id>&session=<session_id>`. On arrival this page
// selects the bot and hands the session to LiveInbox, which opens that
// conversation as soon as the inbox has loaded (or tells the operator it has
// already ended). The params are dropped from the URL once consumed so a
// refresh / back-navigation doesn't re-open the conversation.
const LiveChat = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const { bots, loading, selectedBotId, selectedBotIds, openAddBot } = useSelectedBot();
  const [inboxReady, setInboxReady] = useState(false);
  const handleInboxReady = useCallback(() => setInboxReady(true), []);

  // Captured once per arrival (URL params are stripped after hand-over, so
  // this must not be re-derived from `location` on every render).
  const [deepLink, setDeepLink] = useState(() => parseLiveDeepLink(location.search));

  // In-app navigation to a NEW deep link while already mounted (e.g. a toast
  // for another conversation): pick it up as well.
  useEffect(() => {
    const parsed = parseLiveDeepLink(location.search);
    if (!parsed.sessionId && !parsed.botId) return;
    // Keep the pending object when the same link is still being handled;
    // a genuinely new arrival gets a new object (LiveInbox tracks identity).
    setDeepLink((prev) =>
      prev.botId === parsed.botId && prev.sessionId === parsed.sessionId ? prev : parsed
    );
  }, [location.search]);

  useEffect(() => {
    if (deepLink.botId) selectBotFromDeepLink(deepLink.botId);
  }, [deepLink.botId]);

  const handleDeepLinkConsumed = useCallback(() => {
    setDeepLink({ botId: null, sessionId: null });
    if (location.search) navigate('/live', { replace: true });
  }, [location.search, navigate]);

  const header = (
    <header className="section-head">
      <h1 className="section-head-title">{t('live_chat_page_title')}</h1>
      <p className="section-head-sub">
        {t('live_chat_page_subtitle')}
      </p>
    </header>
  );

  const spinner = (
    <div className="live-inbox-spinner">
      <FontAwesomeIcon icon={faSpinner} spin style={{ color: '#5616ea', fontSize: '20px' }} />
    </div>
  );

  if (loading) {
    return (
      <div className="live-page">
        {header}
        <div className="live-inbox-host">{spinner}</div>
      </div>
    );
  }

  if (!bots.length) {
    return (
      <div className="live-page">
        {header}
        <FirstBotPrompt onAddBot={openAddBot} />
      </div>
    );
  }

  // Coordinated single-spinner reveal: don't show the inbox until the global
  // selection is resolved AND LiveInbox reports ready, so it never flashes its
  // own loading state.
  const selectorReady = selectedBotId !== '' || bots.length === 0;
  const pageReady = selectorReady && inboxReady;

  return (
    <div className="live-page">
      {header}
      <div className="live-inbox-host">
        {!pageReady && spinner}
        <div
          className="live-inbox-fill"
          style={{ visibility: pageReady ? 'visible' : 'hidden' }}
        >
          {selectorReady && (
            <LiveInbox
              bots={bots}
              selectedBotIds={selectedBotIds}
              onReady={handleInboxReady}
              deepLink={deepLink}
              onDeepLinkConsumed={handleDeepLinkConsumed}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default LiveChat;
